import React, { useState, useEffect, useMemo } from 'react';
import { Form, Modal, Select, Spin, Input, Button } from 'antd';
import usePaginatedRecordList from '../../hooks/usePaginatedRecordList';
import { Identifiable, RecordModel } from '../../types/core';
import { ancanaApi } from '../../api/ancanaApi';
import styled from "styled-components";
import { useTheme } from "../../contexts/ThemeProvider";
import useObserveRef from '../../hooks/useObserveRef';

interface ModalSelectProps<T extends Identifiable> {
  formName: string | (string | number)[];
  label: string;
  placeholder?: string;
  required?: boolean;
  recordModel: RecordModel<T>;
  defaultValue?: number[];
  mode?: 'multiple'; // For now only multiple is supported
}

const Container = styled.div`
    .ant-select-selection-item {
      height: unset;
    }
`;

const CustomSelectControl: React.FC<{
  value?: string[],
  onChange?: (value: string[]) => void,
  recordModel: RecordModel<any>,
  placeholder?: string,
  label: string
}> = ({ value, onChange, recordModel, placeholder, label }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalSelectedValues, setModalSelectedValues] = useState(value || []);
  const [prefetchedRecords, setPrefetchedRecords] = useState<any[]>([]);
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [checkedInitialValue, setCheckedInitialValue] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const { currentTheme } = useTheme();

  const { recordList, isLoading, fetch, pagination, fetchNextPage, isFetchingNext } = usePaginatedRecordList<any>({
    recordPath: recordModel.path,
    apiService: recordModel.apiService || 'admin',
    pageSize: '27',
    initialAutoFetch: false,
  });
  const { observedRef } = useObserveRef(isLoading || isFetchingNext || !pagination.next, fetchNextPage);


  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    const query = `${recordModel.selectQuery || '&ordering=-id'}${debouncedSearchTerm ? `&search=${debouncedSearchTerm}` : ''}`;
    if (fetch) {
      fetch({ pagination, queryReplace: query });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!isLoading && value && value.length > 0) {
      const existingIds = new Set(recordList.map(r => r.id));
      const missingIds = value.filter(id => !existingIds.has(id));
      if (missingIds.length > 0) {
        const fetchMissing = async () => {
          setIsPrefetching(true);
          try {
            const url = `${recordModel.path}/?id__in=${missingIds.join(',')}${recordModel.selectQuery || ''}`;
            const { data } = await ancanaApi.get(url);
            const recordsToPrefetch = data.results || data;
            setPrefetchedRecords(prev => {
              const recordMap = new Map();
              prev.forEach(r => recordMap.set(r.id, r));
              recordsToPrefetch.forEach((r: any) => {
                if (r) recordMap.set(r.id, r);
              });
              return Array.from(recordMap.values());
            });
          } catch (e) {
            console.error("Failed to prefetch records", e);
          } finally {
            setIsPrefetching(false);
          }
        };
        fetchMissing();
      }
    }
  }, [isLoading, value, recordList, recordModel.path, checkedInitialValue]);

  const combinedList = useMemo(() => {
    const allRecords = [...prefetchedRecords, ...recordList];
    const recordMap = new Map();
    allRecords.forEach(r => recordMap.set(r.id, r));
    return Array.from(recordMap.values());
  }, [recordList, prefetchedRecords]);

  useEffect(() => {
    setModalSelectedValues(value || []);
  }, [value]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    if (onChange) {
      onChange(modalSelectedValues);
    }
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleCardClick = (selectedValue: number) => {
    const newSelectedValues = modalSelectedValues.includes(selectedValue.toString())
      ? modalSelectedValues.filter(v => v !== selectedValue.toString())
      : [...modalSelectedValues, selectedValue.toString()];
    setModalSelectedValues(newSelectedValues);
  };

  return (
    <Container>
      <div onClick={showModal}>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          value={value}
          open={false}
          showArrow
          placeholder={placeholder}
          loading={isLoading || isPrefetching}
        >
          {combinedList.map(record => (
            <Select.Option key={record.id} value={record.id.toString()} label={recordModel.toString(record)}>
              {recordModel.toDescriptor ? recordModel.toDescriptor(record) : recordModel.toString(record)}
            </Select.Option>
          ))}
        </Select>
      </div>

      <Modal
        title={`Select ${label}`}
        visible={isModalVisible}
        onOk={handleOk}
        okText="Confirm selection"
        onCancel={handleCancel}
        width={950}
        centered
        bodyStyle={{ maxHeight: 'calc(100vh - 115px)', overflowY: 'auto', paddingTop: 0 }}
      >
        <div
          style={{
            position: 'sticky', top: 0, zIndex: 2,
            padding: '12px 16px', margin: '0 -16px 8px -16px',
            background: currentTheme === 'dark' ? '#2a2a2a' : 'white',
            display: 'flex', gap: '12px'
          }}
        >
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
            <Button onClick={() => setModalSelectedValues([])}>Clear selection</Button>
        </div>
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '16px' }}>
            {combinedList.map(record => {
              const isSelected = modalSelectedValues.includes(record.id.toString());
              const selectionIndex = isSelected ? modalSelectedValues.indexOf(record.id.toString()) + 1 : 0;
              return (
                <div
                  key={record.id}
                  onClick={() => handleCardClick(record.id)}
                  style={{
                    position: 'relative',
                    padding: '8px',
                    border: isSelected ? '1px solid #c1862c' : '1px solid rgba(140,140,140,.2)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    backgroundColor: isSelected ? 'rgba(232,166,94,0.15)' : 'rgba(166,166,166,0.05)',
                  }}
                >
                  {isSelected && (
                    <div style={{
                      position: 'absolute',
                      top: -5,
                      left: -5,
                      background: '#c1862c',
                      color: 'white',
                      borderRadius: '50%',
                      width: 22,
                      height: 22,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 'bold',
                      border: '1px solid white',
                      zIndex: 1,
                    }}>
                      {selectionIndex}
                    </div>
                  )}
                  {recordModel.toDescriptor ? recordModel.toDescriptor(record) : recordModel.toString(record)}
                </div>
              );
            })}
          </div>
          <div ref={observedRef} style={{ height: 40, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {(isLoading || isPrefetching || isFetchingNext) && <Spin />}
          </div>
        </div>
      </Modal>
    </Container>
  );
};


const ModalSelect: React.FC<ModalSelectProps<any>> = (props) => {
  const { formName, label, placeholder, required, recordModel, defaultValue } = props;

  return (
    <Form.Item
      name={formName}
      label={label}
      initialValue={defaultValue || []}
      rules={[{ required }]}
    >
      <CustomSelectControl recordModel={recordModel} placeholder={placeholder} label={label} />
    </Form.Item>
  );
};

export default ModalSelect;
